import React, { useState, useEffect, memo } from "react";
import {
  Grid,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Stack,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import configServ from "../../services/config";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { rSetFetchJobBatchList } from "../../redux/reducers/GlobalVariables";

const daysList = [
  { label: "Sun", value: 0 },
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
];

function CreateJobBatchCard({
  isCreate,
  setIsCreate,
  isEdit,
  setIsEdit,
  setIsChanged,
  editData = {},
  setEditData,
}) {
  const { company_id, user_id, is_super, is_admin } = useSelector(
    (state) => state.GlobalVariables
  );

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];

    setForm((prev) => ({
      ...prev,
      start_date: today,
    }));
  }, []);

  const today = new Date().toISOString().split("T")[0];
  const dispatch = useDispatch();

  const getMaxEndDate = () => {
    if (!form.start_date) return "";

    const date = new Date(form.start_date);
    date.setDate(date.getDate() + 90);

    return date.toISOString().split("T")[0];
  };

  const [form, setForm] = useState({
    job_master_id: "",
    customer_id: "",
    site_id: "",
    assigned_to: "",
    start_date: "",
    end_date: "",
    recurrence_type: "daily",
    recurrence_days: [],
    priority: "medium",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // dropdown states
  const [jobMasterList, setJobMasterList] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [siteList, setSiteList] = useState([]);

  // ================= API CALLS =================

  const fetchJobMaster = async () => {
    const res = await configServ.getactivejobMaster({ company_id });
    if (res?.status) setJobMasterList(res.data);
  };

  const fetchCustomer = async () => {
    const res = await configServ.getSatandardActiveCustomer({ company_id });
    if (res?.status) setCustomerList(res.data);
  };

  const fetchStaff = async () => {
    const res = await configServ.getStaffActiveCustomer({
      company_id,
      user_id,
      is_admin: Boolean(is_super || is_admin),
    });
    if (res?.status) setStaffList(res.data);
  };

  const fetchShipToAddress = async (cust_id) => {
    const res = await configServ.getShipToAddress({ cust_id });
    setSiteList(res);
  };

  useEffect(() => {
    if (isCreate) {
      fetchCustomer();
      fetchJobMaster();
      fetchStaff();
    }
  }, [isCreate]);

  // 👉 customer change → site reload
  useEffect(() => {
    if (form.customer_id) {
      fetchShipToAddress(form.customer_id);

      setForm((prev) => ({
        ...prev,
        site_id: "",
      }));
    }
  }, [form.customer_id]);

  // edit fill
  useEffect(() => {
    if (editData?.id) {
      setForm({ ...editData });
    }
  }, [editData]);

  // ================= HANDLERS =================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "start_date" && { end_date: "" }), // reset end date
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDayToggle = (day) => {
    let updated = form.recurrence_days.includes(day)
      ? form.recurrence_days.filter((d) => d !== day)
      : [...form.recurrence_days, day];

    setForm((prev) => ({
      ...prev,
      recurrence_days: updated,
    }));
  };

  const handleReset = () => {
    setForm({
      job_master_id: "",
      customer_id: "",
      site_id: "",
      assigned_to: "",
      start_date: "",
      end_date: "",
      recurrence_type: "daily",
      recurrence_days: [],
      priority: "medium",
    });
    setErrors({});
    setEditData({});
    setIsEdit(false);
  };

  // ================= VALIDATION =================

  const validate = () => {
    let err = {};

    if (!form.job_master_id) err.job_master_id = "Required";
    if (!form.customer_id) err.customer_id = "Required";
    if (!form.site_id) err.site_id = "Required";
    if (!form.assigned_to) err.assigned_to = "Required";
    if (!form.start_date) err.start_date = "Required";
    if (!form.end_date) err.end_date = "Required";

    if (
      form.recurrence_type === "custom" &&
      form.recurrence_days.length === 0
    ) {
      err.recurrence_days = "Select at least 1 day";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  // ================= SUBMIT =================

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await configServ.createJobBatch({
        ...form,
        company_id,
        assigned_by: user_id,
      });

      if (res?.status) {
        toast.success("Job Batch Created");
        handleReset();
        setIsCreate(false);
        setIsChanged((p) => !p);
        dispatch(rSetFetchJobBatchList(true));
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  // ================= UI =================

  return (
    <Box sx={{ backgroundColor: "#e3f3fc", p: 2, borderRadius: 2, m: 1 }}>
      <Grid container spacing={2}>
        {/* Job Master */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Job</InputLabel>
            <Select
              name="job_master_id"
              value={form.job_master_id}
              onChange={handleChange}
              label="Job"
              sx={{ backgroundColor: "white" }}
            >
              {jobMasterList.map((j) => (
                <MenuItem key={j.id} value={j.id}>
                  {j.job_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Customer */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Customer</InputLabel>
            <Select
              name="customer_id"
              value={form.customer_id}
              onChange={handleChange}
              label="Customer"
              sx={{ backgroundColor: "white" }}
            >
              {customerList.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.contact_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Site */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Site</InputLabel>
            <Select
              name="site_id"
              value={form.site_id}
              onChange={handleChange}
              label="Site"
              sx={{ backgroundColor: "white" }}
            >
              {siteList.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.address}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Staff */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Assign To</InputLabel>
            <Select
              name="assigned_to"
              value={form.assigned_to}
              onChange={handleChange}
              label="Assign To"
              sx={{ backgroundColor: "white" }}
            >
              {staffList.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.contact_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Dates */}
        <Grid item xs={12} md={3}>
          <TextField
            type="date"
            label="Start Date"
            name="start_date"
            value={form.start_date}
            onChange={handleChange}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: today, // ❌ past disabled
            }}
            sx={{ backgroundColor: "white" }}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            type="date"
            label="End Date"
            name="end_date"
            value={form.end_date}
            onChange={handleChange}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: form.start_date || today,
              max: getMaxEndDate(), // ✅ 90 days limit
            }}
            sx={{ backgroundColor: "white" }}
          />
        </Grid>

        {/* Recurrence */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Recurrence</InputLabel>
            <Select
              name="recurrence_type"
              value={form.recurrence_type}
              onChange={handleChange}
              label="Recurrence"
              sx={{ backgroundColor: "white" }}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Mon-Fri</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Priority */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Priority</InputLabel>
            <Select
              name="priority"
              value={form.priority}
              onChange={handleChange}
              label="Priority"
              sx={{ backgroundColor: "white" }}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Custom Days */}
        {form.recurrence_type === "custom" && (
          <Grid item xs={12}>
            <Stack direction="row" spacing={1}>
              {daysList.map((d) => (
                <Chip
                  key={d.value}
                  label={d.label}
                  clickable
                  color={
                    form.recurrence_days.includes(d.value)
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleDayToggle(d.value)}
                />
              ))}
            </Stack>
          </Grid>
        )}

        {/* Submit Button */}
        <Grid item xs={12} md={12} textAlign="right">
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            {/* Reset button (only when NOT edit) */}
            <IconButton
              onClick={handleReset}
              sx={{
                backgroundColor: "secondary.main",
                color: "white",
                "&:hover": { backgroundColor: "secondary.dark" },
              }}
              title="Reset"
            >
              <RotateLeftIcon />
            </IconButton>

            {/* Save / Update button */}
            <IconButton
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                backgroundColor: isEdit ? "success.main" : "primary.main",
                color: "white",
                "&:hover": {
                  backgroundColor: isEdit ? "success.dark" : "primary.dark",
                },
              }}
              title={isEdit ? "Update" : "Save"}
            >
              <SaveIcon />
            </IconButton>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

export default memo(CreateJobBatchCard);
